import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  IconButton,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';

import authService from '../services/authService';

interface ModuleFormData {
  name: string;
  description: string;
  auth_endpoint: string | null;
  process_endpoint: string;
  get_details_endpoint: string | null;
  application_token: string;
  is_active: boolean;
  data_structure: any; // Allow any structure for flexibility with nested JSON
}

const schema = yup.object({
  name: yup.string().required('Module name is required'),
  description: yup.string().required('Description is required'),
  auth_endpoint: yup.string().url('Must be a valid URL').nullable().default(null),
  process_endpoint: yup.string().url('Must be a valid URL').required('Process endpoint is required'),
  get_details_endpoint: yup.string().url('Must be a valid URL').nullable().default('https://atlas-test.beo-software.de/alpha/restapi/'),
  application_token: yup.string().default(''),
  is_active: yup.boolean().required(),
  data_structure: yup.mixed().required('Data structure is required'),
});

interface ModuleFormProps {
  open: boolean;
  onClose: (shouldRefresh?: boolean) => void;
  module?: any | null;
}

const ModuleForm: React.FC<ModuleFormProps> = ({ open, onClose, module }) => {
  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ModuleFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      name: '',
      description: '',
      auth_endpoint: null,
      process_endpoint: '',
      get_details_endpoint: 'https://atlas-test.beo-software.de/alpha/restapi/',
      application_token: '',
      is_active: true,
      data_structure: {
        fields: [{ name: '', type: 'string', required: true, description: '' }],
      },
    },
  });

  const dataStructure = watch('data_structure');

  useEffect(() => {
    if (module) {
      reset({
        name: module.name,
        description: module.description,
        auth_endpoint: module.auth_endpoint || null,
        process_endpoint: module.process_endpoint || module.endpoint_url || module.api_endpoint || '',
        get_details_endpoint: module.get_details_endpoint || 'https://atlas-test.beo-software.de/alpha/restapi/',
        application_token: module.application_token || '',
        is_active: module.is_active || module.enabled || true,
        data_structure: module.data_structure || module.json_schema || {
          fields: [{ name: '', type: 'string', required: true, description: '' }],
        },
      });
    } else {
      reset({
        name: '',
        description: '',
        auth_endpoint: null,
        process_endpoint: '',
        get_details_endpoint: 'https://atlas-test.beo-software.de/alpha/restapi/',
        application_token: '',
        is_active: true,
        data_structure: {
          fields: [{ name: '', type: 'string', required: true, description: '' }],
        },
      });
    }
  }, [module, reset]);

  const onSubmit = async (data: ModuleFormData) => {
    try {
      setIsLoading(true);
      
      // Log the data being sent for debugging
      console.log('Submitting module data:', data);
      
      if (module) {
        const result = await authService.updateModule(module.id, data);
        console.log('Update result:', result);
        toast.success('Module updated successfully');
      } else {
        const result = await authService.createModule(data);
        console.log('Create result:', result);
        toast.success('Module created successfully');
      }
      
      onClose(true);
    } catch (error: any) {
      console.error('Full module save error:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      
      let errorMessage = 'Failed to save module';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleJsonImport = (jsonString: string) => {
    try {
      if (!jsonString.trim()) {
        toast.error('JSON cannot be empty');
        return;
      }

      const parsed = JSON.parse(jsonString);

      // Validate that it's a reasonable data structure
      if (typeof parsed !== 'object' || parsed === null) {
        toast.error('Data structure must be a JSON object');
        return;
      }

      setValue('data_structure', parsed);
      toast.success('JSON imported successfully');

      console.log('Imported JSON structure:', parsed);
    } catch (error) {
      console.error('JSON parse error:', error);
      toast.error('Invalid JSON format. Please check your syntax.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {module ? 'Edit Module' : 'Create New Module'}
        <IconButton onClick={() => onClose()} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={3}>
            {/* Basic Information */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Module Name"
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      fullWidth
                    />
                  )}
                />

                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description"
                      multiline
                      rows={3}
                      error={!!errors.description}
                      helperText={errors.description?.message}
                      fullWidth
                    />
                  )}
                />

                <Controller
                  name="auth_endpoint"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Authentication Endpoint URL (Optional)"
                      placeholder="https://api.example.com/auth/login"
                      error={!!errors.auth_endpoint}
                      helperText={errors.auth_endpoint?.message || 'The URL for authentication (leave empty if no separate auth endpoint)'}
                      fullWidth
                    />
                  )}
                />

                <Controller
                  name="process_endpoint"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Process Endpoint URL"
                      placeholder="https://api.example.com/process"
                      error={!!errors.process_endpoint}
                      helperText={errors.process_endpoint?.message || 'The URL where extracted data will be sent for processing'}
                      fullWidth
                      required
                    />
                  )}
                />

                <Controller
                  name="get_details_endpoint"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Get Details Endpoint URL (Optional)"
                      placeholder="https://atlas-test.beo-software.de/alpha/restapi/"
                      error={!!errors.get_details_endpoint}
                      helperText={errors.get_details_endpoint?.message || 'The URL for retrieving detailed information from the module'}
                      fullWidth
                    />
                  )}
                />

                <Controller
                  name="is_active"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      }
                      label="Active Module"
                    />
                  )}
                />
              </Box>
            </Box>

            <Divider />

            {/* Security Configuration */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Security Configuration
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Controller
                  name="application_token"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Application Token"
                      placeholder="Enter application token for API authentication"
                      type="password"
                      error={!!errors.application_token}
                      helperText={errors.application_token?.message || 'Token used for authenticating with the external application (stored encrypted in database)'}
                      fullWidth
                    />
                  )}
                />
                <Typography variant="caption" color="textSecondary">
                  This token will be included in the X-Token header when sending data to the module's API endpoint. 
                  It is encrypted before being stored in the database for security.
                </Typography>
              </Box>
            </Box>

            <Divider />

            {/* Data Structure - JSON Editor Only */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Data Structure (JSON)
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <TextField
                  multiline
                  rows={15}
                  fullWidth
                  variant="outlined"
                  label="JSON Structure"
                  defaultValue={JSON.stringify(dataStructure, null, 2)}
                  onBlur={(e) => handleJsonImport(e.target.value)}
                  helperText="Paste your JSON schema here. Supports nested objects, arrays, and complex data structures."
                  sx={{
                    '& .MuiInputBase-input': {
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                    }
                  }}
                />
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  Examples: {"{"}"name": "string", "age": "number", "address": {"{"}"street": "string", "city": "string"{"}"}{"}"}<br/>
                  Supports any valid JSON structure including nested objects and arrays.
                </Typography>
              </Paper>
            </Box>

            {/* Preview */}
            <Box>
              <Typography variant="h6" gutterBottom>
                JSON Preview
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f5f5f5', maxHeight: '300px', overflow: 'auto' }}>
                <pre style={{ 
                  margin: 0, 
                  fontSize: '0.875rem', 
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {JSON.stringify(dataStructure, null, 2)}
                </pre>
              </Paper>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                This is the data structure that will be sent to the module's API endpoint.
                It supports nested objects and arrays for complex data models.
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => onClose()} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : (module ? 'Update Module' : 'Create Module')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ModuleForm;